import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CompanyAddEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const companyCode = searchParams.get("companyCode");

  const isEdit = Boolean(companyCode);
  const toastShownRef = useRef(false);
  const userId = Number(localStorage.getItem("userId"));

  /* ===================== FORM STATE ===================== */
  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
    authorizedPersonName: "",
    spocPersonName: "",
    gstNumber: "",
    coiNumber: "",
    mobileNumber: "",
    email: "",
    accountNumber:"",
    ifscCode:"",
    userName: "",
    password: "",
    status: "ACTIVE",
  });

  /* ===================== DOCUMENT STATE ===================== */
  const [documents, setDocuments] = useState({
    panFile: null,
    coiFile: null,
    serviceAgreementFile: null,
    kycFile: null,
  });

  const [loading, setLoading] = useState(false);
  const [companyCreated, setCompanyCreated] = useState(false);

  /* ===================== FETCH COMPANY (EDIT) ===================== */
  const fetchCompanyDetails = async () => {
    try {
      const res = await axiosInstance.get(
        `${URL.GET_ALL_COMPANY}?companyCode=${companyCode}`
      );

      if (res.data?.data?.length > 0) {
        const c = res.data.data[0];
        setFormData({
          companyName: c.companyName || "",
          address: c.address || "",
          authorizedPersonName: c.authorizedPersonName || "",
          spocPersonName: c.spocPersonName || "",
          gstNumber: c.gstNumber || "",
          coiNumber: c.coiNumber || "",
          mobileNumber: c.mobileNumber || "",
          email: c.email || "",
          accountNumber: c.accountNumber || "",
          ifscCode: c.ifscCode || "",
          status: c.status || "ACTIVE",
          userName: "",
          password: "",
        });
      }
    } catch (err) {
      console.error("Error fetching company:", err);
    }
  };

  useEffect(() => {
    if (isEdit) fetchCompanyDetails();
  }, [companyCode]);

  /* ===================== INPUT HANDLER ===================== */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ===================== SAVE COMPANY ===================== */
  const handleSaveCompany = async () => {
    if (loading) return;

    try {
      setLoading(true);

      if (isEdit) {
        /* -------- EDIT COMPANY -------- */
        const res = await axiosInstance.post(
          `${URL.UPDATE_COMPANY}?companyCode=${companyCode}`,
          {
            companyName: formData.companyName,
            address: formData.address,
            authorizedPersonName: formData.authorizedPersonName,
            spocPersonName: formData.spocPersonName,
            coiNumber: formData.coiNumber,
            mobileNumber: formData.mobileNumber,
            email: formData.email,
            accountNumber: formData.accountNumber,
            ifscCode: formData.ifscCode,
            status: formData.status,
            updatedBy: userId,
          }
        );

        if (res.data?.error) {
          toast.error(res.data.error);
          setLoading(false);
          return;
        }

        if (!toastShownRef.current) {
          toast.success(res.data?.message || "Company updated successfully");
          toastShownRef.current = true;
        }

        setTimeout(() => navigate(-1), 1200);
      } else {
        /* -------- ADD COMPANY -------- */
const res = await axiosInstance.post(URL.ADD_COMPANY, {
  companyName: formData.companyName,
  address: formData.address,
  authorizedPersonName: formData.authorizedPersonName,
  spocPersonName: formData.spocPersonName,
  gstNumber: formData.gstNumber,
  coiNumber: formData.coiNumber,
  mobileNumber: formData.mobileNumber,
  email: formData.email,
  accountNumber: formData.accountNumber,
  ifscCode: formData.ifscCode,
  userName: formData.userName,
  password: formData.password,
  createdBy: userId,
});

/* 🔹 CHECK API ERROR (because API returns 200 with error) */
if (res.data?.error) {
  toast.error(res.data.error);
  setLoading(false);   // ✅ re-enable button
  return;
}

if (!toastShownRef.current) {
  toast.success(res.data?.message || "Company added successfully");
  toastShownRef.current = true;
}

setCompanyCreated(true);
      }
    } catch (err) {
  console.error(err);

  const errorMessage =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    "Something went wrong";

  toast.error(errorMessage);
  setLoading(false);   // ✅ re-enable button
}
  };

  /* ===================== UPLOAD DOCUMENTS ===================== */
  const handleUploadDocuments = async () => {
    try {
      const fd = new FormData();
      fd.append("gstNumber", formData.gstNumber);

      if (documents.panFile) fd.append("panFile", documents.panFile);
      if (documents.coiFile) fd.append("coiFile", documents.coiFile);
      if (documents.serviceAgreementFile)
        fd.append("serviceAgreementFile", documents.serviceAgreementFile);
      if (documents.kycFile) fd.append("kycFile", documents.kycFile);

      await axiosInstance.post(URL.ADD_COMPANY_DOCUMENTS, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Documents uploaded successfully");
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      console.error(err);
      toast.error("Document upload failed");
    }
  };

  return (
    <div className="container">
      <h3 className="mb-3">
        {isEdit ? "Edit Company" : "Add Company"}
      </h3>

      <div className="card p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Company Name</label>
            <input
              name="companyName"
              className="form-control"
              value={formData.companyName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">GST Number</label>
            <input
              name="gstNumber"
              className="form-control"
              value={formData.gstNumber}
              onChange={handleChange}
              disabled={isEdit}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">COI Number</label>
            <input
              name="coiNumber"
              className="form-control"
              value={formData.coiNumber}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Authorized Person</label>
            <input
              name="authorizedPersonName"
              className="form-control"
              value={formData.authorizedPersonName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">SPOC Person</label>
            <input
              name="spocPersonName"
              className="form-control"
              value={formData.spocPersonName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Mobile</label>
            <input
              name="mobileNumber"
              className="form-control"
              value={formData.mobileNumber}
              onChange={handleChange}
            />
          </div>

            <div className="col-md-6">
            <label className="form-label">Account Number</label>
            <input
              name="accountNumber"
              className="form-control"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </div>

           <div className="col-md-6">
            <label className="form-label">IFSC Code</label>
            <input
              name="ifscCode"
              className="form-control"
              value={formData.ifscCode}
              onChange={handleChange}
            />
          </div>


          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          {/* -------- STATUS ONLY IN EDIT -------- */}
          {isEdit && (
            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
          )}

          {!isEdit && (
            <>
              <div className="col-md-6">
                <label className="form-label">Username</label>
                <input
                  name="userName"
                  className="form-control"
                  value={formData.userName}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Password</label>
                <div className="input-group">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="col-md-12">
            <label className="form-label">Address</label>
            <textarea
              name="address"
              className="form-control"
              rows="2"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="mt-4 d-flex gap-2">
          <button
            className="btn btn-primary"
            onClick={handleSaveCompany}
            disabled={loading}
          >
            {isEdit ? "Update Company" : "Save Company"}
          </button>

          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </div>

    {/* ===================== DOCUMENT UPLOAD ===================== */}
{!isEdit && companyCreated && (
  <div className="card p-4 mt-4">
    <h5 className="mb-3">Upload Company Documents</h5>

    {/* PAN */}
    <div className="mb-3">
      <label className="form-label">
        PAN Document <span className="text-muted">(PDF / JPG / PNG)</span>
      </label>
      <input
        type="file"
        className="form-control"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) =>
          setDocuments({ ...documents, panFile: e.target.files[0] })
        }
      />
    </div>

    {/* COI */}
    <div className="mb-3">
      <label className="form-label">
        Certificate of Incorporation (COI)
        <span className="text-muted"> (PDF)</span>
      </label>
      <input
        type="file"
        className="form-control"
        accept=".pdf"
        onChange={(e) =>
          setDocuments({ ...documents, coiFile: e.target.files[0] })
        }
      />
    </div>

    {/* Service Agreement */}
    <div className="mb-3">
      <label className="form-label">
        Service Agreement
        <span className="text-muted"> (PDF)</span>
      </label>
      <input
        type="file"
        className="form-control"
        accept=".pdf"
        onChange={(e) =>
          setDocuments({
            ...documents,
            serviceAgreementFile: e.target.files[0],
          })
        }
      />
    </div>

    {/* KYC */}
    <div className="mb-3">
      <label className="form-label">
        KYC Document
        <span className="text-muted"> (PDF / JPG / PNG)</span>
      </label>
      <input
        type="file"
        className="form-control"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) =>
          setDocuments({ ...documents, kycFile: e.target.files[0] })
        }
      />
    </div>

    <button
      className="btn btn-success mt-2"
      onClick={handleUploadDocuments}
    >
      Upload Documents
    </button>
  </div>
)}
      {/* 🔔 ToastContainer – as you asked */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
  );
};

export default CompanyAddEdit;